/**
 * Status do serviço (§21.12).
 *
 * A ÚNICA COISA QUE UMA PÁGINA DE STATUS NÃO PODE FAZER é dizer "tudo bem"
 * quando não sabe. Por isso ela tem três estados, e não dois:
 *
 *   - CONSULTANDO — ainda não sabe, e diz isso;
 *   - RESPONDENDO — a API respondeu, com a versão que está no ar;
 *   - SEM RESPOSTA — a consulta falhou, e ela diz que não conseguiu falar com o
 *     sistema, em vez de traduzir o próprio silêncio como boa notícia.
 *
 * Ela também não inventa histórico de incidentes: o que ela mostra é o AGORA,
 * verificado no momento em que a página abriu.
 */

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { RodapePublico } from '../components/RodapePublico';
import { t } from '../lib/i18n';

/*
 * Uma falha, duas causas — e um só desenho na tela (sem estado visual novo):
 *   - 'indisponivel': a API RESPONDEU e disse que não está pronta (o 503 do
 *     readiness, banco inalcançável) — "Serviço indisponível";
 *   - 'sem-resposta': ninguém da aplicação respondeu (rede, tempo esgotado, ou
 *     o proxy devolvendo a página de erro dele) — "Sem resposta".
 */
type Situacao =
  | { estado: 'consultando' }
  | { estado: 'respondendo'; banco: string; versao: string | null; ambiente: string | null }
  | { estado: 'falha'; motivo: 'indisponivel' | 'sem-resposta' };

/** Acima disso, quem abriu a página já merece uma resposta: "Sem resposta". */
const LIMITE_DA_CONSULTA_MS = 10_000;

/**
 * O READINESS É LIDO DIRETO, E NÃO PELO `api.get`.
 *
 * O cliente do CRM transforma qualquer erro sem o envelope `{error}` no mesmo
 * `GATEWAY_UNAVAILABLE` e descarta o corpo — e o 503 do readiness
 * (`{status:'unavailable', database:'error'}`) chega exatamente assim, igual ao
 * 502/503 em HTML do nginx quando a aplicação está fora. Só o corpo separa
 * "a API respondeu que não está pronta" de "a API não respondeu".
 */
async function consultarProntidao(
  signal: AbortSignal,
): Promise<{ pronta: true; banco: string } | { pronta: false; motivo: 'indisponivel' | 'sem-resposta' }> {
  let resposta: Response;
  try {
    resposta = await fetch('/health/ready', { signal, headers: { accept: 'application/json' } });
  } catch {
    return { pronta: false, motivo: 'sem-resposta' };
  }
  type CorpoDaProntidao = { status?: unknown; database?: unknown; error?: unknown } | null;
  let corpo: CorpoDaProntidao = null;
  try {
    corpo = (await resposta.json()) as CorpoDaProntidao;
  } catch {
    // Sem JSON é página de erro de quem está no meio do caminho, não da API.
    return { pronta: false, motivo: 'sem-resposta' };
  }
  if (resposta.ok && corpo?.status === 'ready') {
    return { pronta: true, banco: String(corpo.database) };
  }
  if (corpo?.status === 'unavailable' || corpo?.error !== undefined) {
    return { pronta: false, motivo: 'indisponivel' };
  }
  return { pronta: false, motivo: 'sem-resposta' };
}

export function StatusPage(): JSX.Element {
  const [situacao, setSituacao] = useState<Situacao>({ estado: 'consultando' });

  useEffect(() => {
    let vivo = true;
    // O mesmo sinal serve ao tempo esgotado e à saída da página; `vivo` separa
    // os dois casos na hora de escrever o estado.
    const controle = new AbortController();
    const relogio = setTimeout(() => controle.abort(), LIMITE_DA_CONSULTA_MS);
    void (async () => {
      // A versão é informativa e corre em paralelo: a falha dela nunca decide a
      // saúde do sistema — quem decide é só o readiness.
      const versao = api
        .get<{ version: string; environment: string }>('/version', controle.signal, { absolute: true })
        .then((v) => ({ versao: v.version, ambiente: v.environment }))
        .catch(() => ({ versao: null, ambiente: null }));
      const prontidao = await consultarProntidao(controle.signal);
      if (!prontidao.pronta) {
        if (vivo) setSituacao({ estado: 'falha', motivo: prontidao.motivo });
        return;
      }
      const v = await versao;
      if (vivo) setSituacao({ estado: 'respondendo', banco: prontidao.banco, ...v });
    })().finally(() => clearTimeout(relogio));
    return () => {
      vivo = false;
      clearTimeout(relogio);
      controle.abort();
    };
  }, []);

  return (
    <div className="mx-auto min-h-screen max-w-3xl px-4 py-10">
      <h1 className="text-2xl font-semibold text-ink">{t('status.titulo')}</h1>
      <p className="mt-2 text-xs text-ink-muted">{t('status.verificadoAgora')}</p>

      <div className="mt-6 rounded-lg border border-line bg-surface p-4">
        {situacao.estado === 'consultando' && (
          <p className="text-sm text-ink-muted" role="status">{t('status.consultando')}</p>
        )}

        {situacao.estado === 'respondendo' && (
          <div>
            <p className="text-sm font-medium text-positive-700">{t('status.respondendo')}</p>
            <dl className="mt-2 space-y-1 text-2xs text-ink-muted">
              <div className="flex gap-2">
                <dt className="text-ink-subtle">{t('status.banco')}</dt>
                <dd>{situacao.banco}</dd>
              </div>
              {situacao.versao !== null && (
                <div className="flex gap-2">
                  <dt className="text-ink-subtle">{t('status.versaoNoAr')}</dt>
                  <dd>{situacao.versao}</dd>
                </div>
              )}
              {situacao.ambiente !== null && (
                <div className="flex gap-2">
                  <dt className="text-ink-subtle">{t('status.ambiente')}</dt>
                  <dd>{situacao.ambiente}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {situacao.estado === 'falha' && (
          <div>
            <p className="text-sm font-medium text-danger-700">
              {t(situacao.motivo === 'indisponivel' ? 'status.indisponivel' : 'status.semResposta')}
            </p>
            <p className="mt-1 text-2xs text-ink-muted">
              {t(situacao.motivo === 'indisponivel' ? 'status.indisponivelTexto' : 'status.semRespostaTexto')}
            </p>
          </div>
        )}
      </div>

      <RodapePublico />
    </div>
  );
}
